import { updateCampaigns } from 'actions/campains'
import {
  PrimarySmallButton,
  SecondarySmallButton
} from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import {
  features,
  useUnlockService
} from 'components/ReusableComponents/UnlockServiceDialog'
import MediaUploaderMini from 'features/ManageCampaigns/MediaUploaderMini'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import campaignApi from 'services/api/campaign'

import styles from './CampaignControls.module.scss'

const CampaignControls = ({
  showEditPromoDialog,
  OTADiscount,
  nonOTADiscount,
  commonCampaignMedia,
  contentSubsidiaryId,
  revertCampaignsToDefault,
  hasOTADiscount,
  hasNonOTADiscount
}) => {
  const mediaTag = mediaTags.campaign

  const [campaignMedia, setCampaignMedia] = useState(commonCampaignMedia)

  const [showRevertConfirmDialog, setShowRevertConfirmDialog] = useState(false)

  const { addToast } = useToasts()

  const dispatch = useDispatch()

  const firstRenderRef = useRef(true)

  useEffect(() => {
    if (campaignMedia && !firstRenderRef.current) {
      campaignApi
        .updateAllMediaURL({ messageMediaUrls: [campaignMedia] })
        .then(() => {
          setCampaignMedia(campaignMedia)
        })
        .then(() => {
          dispatch(updateCampaigns(contentSubsidiaryId))
        })
        .catch(() => {
          addToast('Failed to update campaign attachment', { appearance: 'error' })
          setCampaignMedia(null)
        })
    }

    firstRenderRef.current = false
  }, [campaignMedia, addToast, contentSubsidiaryId, dispatch])

  function removeMedia() {
    campaignApi
      .updateAllMediaURL({ messageMediaUrls: [null] })
      .then(() => {
        setCampaignMedia(null)
      })
      .then(() => {
        dispatch(updateCampaigns(contentSubsidiaryId))
      })
      .catch(() => {
        addToast('Failed to update campaign attachment', { appearance: 'error' })
      })
  }

  function enableAllCampaigns() {
    campaignApi
      .enableAllCampaigns()
      .then(() => {
        dispatch(updateCampaigns(contentSubsidiaryId))
      })
      .then(() => {
        addToast('All campaigns are enabled', { appearance: 'success' })
      })

      .catch(() => {
        addToast('Failed to enable campaigns', { appearance: 'error' })
      })
  }

  const { hasMarketingService, openUnlockServiceDialog } = useUnlockService()

  return (
    <div className={styles.wrapper}>
      {showRevertConfirmDialog && (
        <ConfirmationDialog
          title="Revert to suggested content"
          positiveMessage="Confirm"
          negativeMessage="Cancel"
          onConfirm={revertCampaignsToDefault}
          onReject={() => setShowRevertConfirmDialog(false)}
        >
          This will replace all campaign content with suggested content.
        </ConfirmationDialog>
      )}
      <div className={styles.controlCol}>
        <div className={styles.controlRow}>
          <div className={styles.controlLabel}>Promo for OTA guests</div>
          <div className={styles.controlButton}>
            {hasOTADiscount ? (
              <PrimarySmallButton
                onClick={() => showEditPromoDialog('OTA')}
                className={styles.primaryControlButton}
              >
                {OTADiscount > 0 ? `${OTADiscount}% discount` : 'Edit promotion'}
              </PrimarySmallButton>
            ) : (
              <SecondarySmallButton
                onClick={() => showEditPromoDialog('OTA')}
                className={styles.secondaryControlButton}
              >
                Add promo
              </SecondarySmallButton>
            )}
          </div>
        </div>
        <div className={styles.controlRow}>
          <div className={styles.controlLabel}>Promo for direct guests</div>
          <div className={styles.controlButton}>
            {hasNonOTADiscount ? (
              <PrimarySmallButton
                onClick={() => showEditPromoDialog('nonOTA')}
                className={styles.primaryControlButton}
              >
                {nonOTADiscount > 0 ? `${nonOTADiscount}% discount` : 'Edit promotion'}
              </PrimarySmallButton>
            ) : (
              <SecondarySmallButton
                onClick={() => showEditPromoDialog('nonOTA')}
                className={styles.secondaryControlButton}
              >
                Add promo
              </SecondarySmallButton>
            )}
          </div>
        </div>
        <button
          onClick={() =>
            hasMarketingService
              ? setShowRevertConfirmDialog(true)
              : openUnlockServiceDialog(features.Campaigns)
          }
          className={styles.controlRowButton}
        >
          Revert to suggested content
        </button>
        <button
          className={styles.controlRowButton}
          onClick={() =>
            hasMarketingService
              ? enableAllCampaigns
              : openUnlockServiceDialog(features.Campaigns)
          }
        >
          Enable all campaigns
        </button>
      </div>
      <div className={styles.textAttachementCol}>
        <p className={styles.textAttachementColTitle}>Media attachment</p>
        <MediaUploaderMini
          selectedMedia={campaignMedia}
          setSelectedMedia={setCampaignMedia}
          removeSelectedMedia={removeMedia}
          mediaTag={mediaTag}
          hasBigUploader={false}
          hasTitle={false}
          className={styles.fileUploader}
          acceptedFileTypes={[
            'image/*',
            'application/pdf',
            'application/rtf',
            'text/calendar',
            'text/vcard',
            'video/*'
          ]}
        />
      </div>
    </div>
  )
}

export default CampaignControls
